import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import Loader from "../../Common/Loader";
import { useDispatch } from "react-redux";
import { updateChequeDetails } from "../../Redux/Actions/DashOptions";
import EdwinolaSunshine from "../Cheque/ChequeFormats/EdwinolaSunshine";
import FoSunshine from "../Cheque/ChequeFormats/FoSunshine";
import UnitedHealthCare from "../Cheque/ChequeFormats/UnitedHealthCare";

const ChequeDetailsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch()
  const location = useLocation();
  const { editChequeData } = location.state || {};

  const [editableData, setEditableData] = useState({});
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchRawData = async () => {
      setLoading(true)
      if (editChequeData?.raw_data_path) {
        try {
          const fullUrl = `${process.env.REACT_APP_LARAVEL_API_URL_DOCUMENT}/${editChequeData.raw_data_path}`;
          const res = await axios.get(fullUrl);
          setLoading(false)
          if (res.data && typeof res.data === "object") {
            setEditableData(JSON.parse(JSON.stringify(res.data)));
            setLoading(false)
          } else {
            setLoading(false)
            toast.error("Invalid data format");
          }
        } catch (error) {
          toast.error("Failed to fetch cheque data");
          setLoading(false)
        }
      } else {
        setLoading(false)
      }
    };
    fetchRawData();
  }, [editChequeData]);

  const handleBack = () => {
    const fromChequeList = new URLSearchParams(location.search).get("details");
    navigate(fromChequeList === "cheqdetails" ? "/dashboard/chequelist" : "/dashboard/billinglisting");
  };

  const handleFieldChange = (pageKey, patientIndex, key, value) => {
    const updated = { ...editableData };
    updated[pageKey][patientIndex][key] = value;
    setEditableData(updated);
  };

  const handleRowChange = (pageKey, patientIndex, rowIndex, key, value) => {
    const updated = { ...editableData };
    updated[pageKey][patientIndex].table[rowIndex][key] = value;
    setEditableData(updated);
  };

  const handleSubtotalChange = (pageKey, patientIndex, key, value) => {
    const updated = { ...editableData };
    const patientData = updated[pageKey][patientIndex];

    // CASE 1: Subtotal is inside table (Edwinola Sunshine)
    const table = patientData.table;
    const subtotalIndex = table?.findIndex(row => row.subtotal);

    if (subtotalIndex !== -1 && table?.[subtotalIndex]?.subtotal) {
      table[subtotalIndex].subtotal[key] = value;
    }

    // CASE 2: Subtotal is a separate object on patient (FO Sunshine)
    else if (patientData.subtotal) {
      patientData.subtotal[key] = value;
    }

    setEditableData(updated);
  };

  const validateEditableData = () => {
    const requiredFields = ["Member No", "Patient Name", "Claim Number", "Mbr No", "MRN"];

    for (const [pageKey, patients] of Object.entries(editableData)) { 
      for (let patientIndex = 0; patientIndex < patients.length; patientIndex++) {
        const patient = patients[patientIndex];

        // ✅ Only validate these requiredFields keys
        for (const key of requiredFields) {
          const value = patient[key];
          if (value === null || value === "") {
            toast.error(`Please fill in ${key} for patient on page ${pageKey}`);
            return false;
          }
        }

        // ✅ Validate .table rows (same as before)
        if (Array.isArray(patient.table)) {
          for (let row of patient.table) {
            if (!row.subtotal) {
              for (const cellVal of Object.values(row)) {
                if (cellVal === null || cellVal === "") {
                  return false;
                }
              }
            } else if (row.subtotal) {
              for (const cellVal of Object.values(row.subtotal)) {
                if (cellVal === null || cellVal === "") {
                  return false;
                }
              }
            }
          }
        }

        // ✅ Validate Tables + Subtotal (same as before)
        if (Array.isArray(patient.Tables)) {
          for (let table of patient.Tables) {
            for (let row of table.Rows || []) {
              for (const cellVal of Object.values(row)) {
                if (cellVal === null || cellVal === "") {
                  return false;
                }
              }
            }

            for (const cellVal of Object.values(table["Sub-total"] || {})) {
              if (cellVal === null || cellVal === "") {
                return false;
              }
            }
          }
        }
      }
    }

    return true;
  };


  const handleSubmitDetails = () => {
    const isValid = validateEditableData();
    if (!isValid) {
      toast.error("Please fill in all required fields before submitting.");
      return;
    }
    setLoading(true)
    const data = {};

    Object.entries(editableData || {}).forEach(([pageKey, patients]) => {
      data[pageKey] = (patients || []).map((patient) => {
        const patientCopy = { ...patient };

        // If EdwinolaSunshine format (has .table key)
        if (patientCopy.table) {
          const allRows = patientCopy.table || [];
          const rows = allRows.filter(row => !row.subtotal);
          const subtotalRow = allRows.find(row => row.subtotal)?.subtotal || {};

          return {
            ...Object.fromEntries(Object.entries(patientCopy).filter(([k]) => k !== "table")),
            table: rows,
            subtotal: subtotalRow,
          };
        }

        // For FoSunshine and UnitedHealthCare (has .Tables key)
        const { Tables = [], ...otherFields } = patientCopy;
        const formattedTables = Tables.map((table) => ({
          Header: table.Header || [],
          Rows: table.Rows || [],
          ["Sub-total"]: table["Sub-total"] || {},
        }));

        return {
          ...otherFields,
          Tables: formattedTables,
        };
      });
    });

    const payload = {
      client_id: editChequeData?.client?.id,
      provider_id: editChequeData?.provider?.id,
      cheque_type_id: editChequeData?.cheque_type?.id,
      cheque_number: editChequeData?.cheque_number,
      cheque_id: editChequeData?.id,
      data,
    };

    dispatch(updateChequeDetails(payload)).then((res) => {
      setLoading(false)
      if (res?.payload?.status === "success") {
        toast.success(res?.payload?.message || "Cheque details submitted successfully!");
        navigate("/dashboard/chequelist");
      } else {
        toast.error(res?.payload?.message || "Failed to submit cheque details.");
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {loading && (<Loader />)}
      <div className="bg-white shadow-lg rounded-xl p-6 space-y-8">
        {/* Header */}
        <div className="flex justify-between">
          <div>
            <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-pink-600 drop-shadow-lg">
              Cheque Details
            </h2>
            <p className="text-lg italic text-pink-600 tracking-wide drop-shadow-sm">
              This was generated by AI — please check carefully.
            </p>
          </div>
          <div className="pt-4">
            <button
              onClick={handleBack}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-lg transition"
            >
              Back
            </button>
          </div>
        </div>
        {(editChequeData?.cheque_type?.slug_name === "fosunshine" || editChequeData?.cheque_type?.id === 1) && (
          <>
            {Object.entries(editableData).map(([pageKey, patientArray]) =>
              Array.isArray(patientArray)
                ? patientArray.map((patient, patientIndex) => (
                  <FoSunshine
                    key={`${pageKey}-${patientIndex}`}
                    pageKey={pageKey}
                    patient={patient}
                    patientIndex={patientIndex}
                    handleFieldChange={handleFieldChange}
                    handleRowChange={handleRowChange}
                    handleSubtotalChange={handleSubtotalChange}
                  />
                ))
                : null
            )}
          </>
        )}
        {(editChequeData?.cheque_type?.slug_name === "edwinolasunshine" || editChequeData?.cheque_type?.id === 3) && (
          <>
            {Object.entries(editableData).map(([pageKey, patientArray]) =>
              Array.isArray(patientArray)
                ? patientArray.map((patient, patientIndex) => (
                  <EdwinolaSunshine
                    key={`${pageKey}-${patientIndex}`}
                    pageKey={pageKey}
                    patient={patient}
                    patientIndex={patientIndex}
                    handleFieldChange={handleFieldChange}
                    handleRowChange={handleRowChange}
                    handleSubtotalChange={handleSubtotalChange}
                  />
                ))
                : null
            )}
          </>
        )}
        {(editChequeData?.cheque_type?.slug_name === "unitedhealthcare" || editChequeData?.cheque_type?.id === 2) && (
          <>
            {Object.entries(editableData).map(([pageKey, patientArray]) =>
              Array.isArray(patientArray)
                ? patientArray.map((patient, patientIndex) => (
                  <UnitedHealthCare
                    key={`${pageKey}-${patientIndex}`}
                    pageKey={pageKey}
                    patient={patient}
                    patientIndex={patientIndex}
                    handleFieldChange={handleFieldChange}
                    handleRowChange={handleRowChange}
                    handleSubtotalChange={handleSubtotalChange}
                  />
                ))
                : null
            )}
          </>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            onClick={handleSubmitDetails}
            className="px-6 py-2 text-white font-semibold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 
              rounded-lg shadow-md hover:shadow-lg hover:scale-105 transition duration-300 ease-in-out"
          >
            Submit Cheque Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChequeDetailsPage;
